/**
 * Die Ziffernfelder (Spec 46b, „Blatt Stand erfassen", Punkt 2): eine Zelle je
 * Stelle, der letzte Stand grau als Platzhalter, Eingabe von rechts. Die Zellen
 * sind Anzeige; getippt wird in **ein** unsichtbares Eingabefeld, das die ganze
 * Fläche deckt — ein Klick auf irgendeine Zelle fokussiert es, Tastatur
 * (Ziffern, Rücktaste), Einfügen und die Zahlentastatur am Telefon laufen
 * dadurch über dasselbe Element. Die Logik steht in `reading-draft.ts`.
 *
 * Genutzt vom Erfassen-Blatt (`meter-capture-*`) und für Endstand und
 * Anfangsstand im Tausch-Blatt (`meter-replace-end-value-*`, `…-start-value-*`).
 */

import type { Ref } from 'react'

import { cells, deleteDigit, typedFromInput } from '@/features/energy/reading-draft'

interface DigitFieldsProps {
  /** Stellenzahl des Zählers = Anzahl der Zellen. */
  readonly digits: number
  readonly typed: string
  readonly onTypedChange: (typed: string) => void
  /** Platzhalter ohne Eingabe; `null` = noch kein Stand („–"). */
  readonly lastValue: number | null
  readonly unit: string
  /** Zellen `<idPrefix>-digit-<i>`, Eingabe `<idPrefix>-input`. */
  readonly idPrefix: string
  /** Bezeichner des Containers; Vorgabe `idPrefix`. */
  readonly containerTestId?: string
  /** Beschriftung für Hilfstechnik („Zählerstand", „Endstand alter Zähler" …). */
  readonly label: string
  readonly inputRef?: Ref<HTMLInputElement>
  readonly disabled?: boolean
}

export function DigitFields({
  digits,
  typed,
  onTypedChange,
  lastValue,
  unit,
  idPrefix,
  containerTestId,
  label,
  inputRef,
  disabled = false,
}: DigitFieldsProps) {
  return (
    <div
      role="group"
      aria-label={label}
      data-testid={containerTestId ?? idPrefix}
      className="group relative flex items-center gap-2"
    >
      <div aria-hidden="true" className="flex min-w-0 flex-1 gap-1 sm:max-w-[420px] sm:flex-none sm:gap-1.5">
        {cells(typed, digits, lastValue).map((cell, index) => (
          <span
            key={index}
            data-testid={`${idPrefix}-digit-${String(index)}`}
            data-kind={cell.kind}
            data-numeric
            className={`border-border bg-surface rounded-card text-page font-(--font-weight-page) flex h-11 min-w-0 flex-1 items-center justify-center border tabular-nums transition-colors duration-(--duration-fast) ease-(--ease-out) sm:h-[52px] sm:w-10 sm:flex-none ${
              cell.kind === 'typed' ? 'text-text' : 'text-muted'
            } ${index === digits - 1 ? 'group-focus-within:border-accent' : ''}`}
          >
            {cell.digit}
          </span>
        ))}
      </div>
      <span className="text-body text-muted shrink-0">{unit}</span>

      <input
        ref={inputRef}
        data-testid={`${idPrefix}-input`}
        aria-label={label}
        type="text"
        inputMode="numeric"
        pattern="[0-9]*"
        autoComplete="off"
        disabled={disabled}
        value={typed}
        onChange={(event) => {
          onTypedChange(typedFromInput(event.target.value, digits))
        }}
        onKeyDown={(event) => {
          // Die Rücktaste nimmt immer rechts weg, egal wo der Cursor steht.
          if (event.key !== 'Backspace' && event.key !== 'Delete') return
          event.preventDefault()
          onTypedChange(deleteDigit(typed))
        }}
        onFocus={(event) => {
          const end = event.currentTarget.value.length
          event.currentTarget.setSelectionRange(end, end)
        }}
        className="absolute inset-0 size-full cursor-text opacity-0 disabled:cursor-default"
      />
    </div>
  )
}
